import React, { useEffect } from "react";
import { motion } from "framer-motion";

export default function OverviewUCTC({ 
  accent = "#115e32",
  pageTitle = "Overview | University of Creative Technology Chittagong",
}) {
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
  return (
    <div className="relative w-full min-h-screen bg-gradient-to-b from-[#B7EAFC] via-[#F0FCFF] to-[#F0FCFF]">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-[#B7EAFC] via-[#F0FCFF] to-[#F0FCFF]">
        <div className="absolute inset-0 flex justify-center items-center opacity-10">
          {/* SVG Decorative Pattern */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="600"
            height="600"
            fill="none"
            viewBox="0 0 600 600"
            className="animate-pulse"
          >
            <circle
              cx="300"
              cy="300"
              r="250"
              stroke="#d4af37"
              strokeWidth="3"
              strokeDasharray="10 10"
            />
            <circle
              cx="300"
              cy="300"
              r="150"
              stroke="#2563eb"
              strokeWidth="2"
              strokeDasharray="6 6"
            />
          </svg>
        </div>

        <div className="relative container mx-auto px-6 py-20 text-center">
          <motion.h1
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1 }}
            className="text-4xl md:text-6xl font-extrabold text-gray-800"
          >
            Overview of{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#2563eb]">
              UCTC
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="mt-6 max-w-3xl mx-auto text-lg text-gray-700 leading-relaxed"
          >
            The University of Creative Technology Chittagong thrives as a dynamic hub of learning dedicated to serving both the nation and humanity. 
          </motion.p>
        </div>
      </section>

      {/* Content Section */}
      <section className="relative container mx-auto px-6 py-16 grid md:grid-cols-2 gap-12 items-center">
        {/* Left Side Content */}
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ duration: 1 }}
          className="space-y-6 text-gray-700"
        >
          <h2 className="text-3xl font-bold text-[#2563eb]">Our Mission</h2>
          <p className="leading-relaxed">
            Our educational mission is to equip students for purposeful lives and to tackle the future's challenges. With the goal of attracting the most talented students nationwide, we are committed to delivering an educational journey that enhances intellect, hones skills, broadens horizons, and cultivates leadership qualities.
          </p>
          <p className="leading-relaxed">
            Established in <span className="font-semibold text-[#d4af37]">2015</span>, UCTC was founded to provide access to superior education for students from low-income households and to deliver academic excellence that aligns with international standards.
          </p>
        </motion.div>

        {/* Right Side Illustration */}
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ duration: 1 }}
          className="flex justify-center"
        >
          {/* Abstract SVG Illustration */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="350"
            height="350"
            viewBox="0 0 350 350"
            className="drop-shadow-lg"
          >
            <circle cx="175" cy="175" r="160" fill="#2563eb" opacity="0.1" />
            <path
              d="M175 40C240 70 310 120 280 200C250 280 100 280 70 200C40 120 110 70 175 40Z"
              fill="#d4af37"
              opacity="0.6"
            />
            <circle cx="175" cy="175" r="60" fill="#2563eb" opacity="0.8" />
          </svg>
        </motion.div>
      </section>

      {/* Academic Programs */}
      <section className="bg-[#E6FAFE] py-16">
        <div className="container mx-auto px-6 text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-3xl font-bold text-[#015A7A]"
          >
            Academic Structure
          </motion.h2>
          <p className="mt-4 max-w-2xl mx-auto text-gray-600">
            The academic structure of the university encompasses{" "}
            <span className="font-semibold">10 programs</span> across three
            distinct schools: <br /> 
            <span className="text-[#2563eb]">School of Science & Engineering</span>,{" "}
            <span className="text-[#d4af37]">School of Business</span>, and{" "}
            <span className="text-[#2563eb]">School of Arts & Social Sciences</span>.
          </p>
        </div>
      </section>
    </div>
  );
}
